/**
 * @xivdyetools/core - Preset Service
 *
 * Manages preset color palettes for FFXIV glamours.
 * Provides themed color combinations for jobs, seasons, events, and aesthetics.
 *
 * @module services/PresetService
 * @example
 * ```typescript
 * import { PresetService, presetData, DyeService, dyeDatabase } from '@xivdyetools/core';
 *
 * const presetService = new PresetService(presetData);
 * const dyeService = new DyeService(dyeDatabase);
 *
 * // Get all job presets
 * const jobPresets = presetService.getPresetsByCategory('jobs');
 *
 * // Get a preset with resolved dye objects
 * const rdmPreset = presetService.getPresetWithDyes('job-rdm', dyeService);
 * ```
 */
/**
 * Service for managing preset color palettes
 *
 * @example
 * ```typescript
 * const presetService = new PresetService(presetData);
 *
 * // List categories
 * const categories = presetService.getCategories();
 *
 * // Get presets by category
 * const jobPresets = presetService.getPresetsByCategory('jobs');
 *
 * // Search presets
 * const results = presetService.searchPresets('red');
 * ```
 */
export class PresetService {
    data;
    presetById;
    /**
     * Initialize the preset service with preset data
     * @param presetData - Preset data object (import from presets.json)
     */
    constructor(presetData) {
        this.data = presetData;
        // Build lookup map for O(1) access by ID
        this.presetById = new Map(this.data.palettes.map((p) => [p.id, p]));
    }
    // ============================================================================
    // Category Operations
    // ============================================================================
    /**
     * Get all preset categories with metadata
     * @returns Array of category metadata with IDs
     *
     * @example
     * ```typescript
     * const categories = presetService.getCategories();
     * // [{ id: 'jobs', name: 'FFXIV Jobs', description: '...', icon: '⚔️' }, ...]
     * ```
     */
    getCategories() {
        return Object.entries(this.data.categories).map(([id, meta]) => ({
            id,
            ...meta,
        }));
    }
    /**
     * Get metadata for a specific category
     * @param category - Category identifier
     * @returns Category metadata or undefined if not found
     *
     * @public
     */
    getCategoryMeta(category) {
        return this.data.categories[category];
    }
    // ============================================================================
    // Preset Retrieval
    // ============================================================================
    /**
     * Get all presets
     * @returns Array of all preset palettes
     */
    getAllPresets() {
        return [...this.data.palettes];
    }
    /**
     * Get all presets in a specific category
     * @param category - Category to filter by
     * @returns Array of presets in that category
     *
     * @example
     * ```typescript
     * const jobPresets = presetService.getPresetsByCategory('jobs');
     * // Returns all job-themed presets (RDM, BLM, WHM, etc.)
     * ```
     */
    getPresetsByCategory(category) {
        return this.data.palettes.filter((p) => p.category === category);
    }
    /**
     * Get a specific preset by ID
     * @param id - Preset identifier (e.g., "job-rdm")
     * @returns Preset or undefined if not found
     *
     * @example
     * ```typescript
     * const rdm = presetService.getPreset('job-rdm');
     * if (rdm) {
     *   console.log(rdm.name); // "Red Mage"
     *   console.log(rdm.dyes); // [5738, 13115, 13117, 5729]
     * }
     * ```
     */
    getPreset(id) {
        return this.presetById.get(id);
    }
    // ============================================================================
    // Search Operations
    // ============================================================================
    /**
     * Search presets by name or tags
     * @param query - Search query (case-insensitive)
     * @returns Array of matching presets
     *
     * @example
     * ```typescript
     * // Search by name
     * const results = presetService.searchPresets('red');
     * // Returns: Red Mage, Blood Red themed presets, etc.
     *
     * // Search by tag
     * const tankResults = presetService.searchPresets('tank');
     * // Returns: Paladin, Warrior, Dark Knight, Gunbreaker
     * ```
     */
    searchPresets(query, dyeService) {
        const lowerQuery = query.toLowerCase().trim();
        if (!lowerQuery) {
            return [];
        }
        // With a dye service, a palette also matches on the names of the dyes it
        // contains — "search presets, dyes, tags" is only true if dyes count.
        const matchesDye = (p) => {
            if (!dyeService)
                return false;
            return p.dyes.some((stainId) => dyeService.getByStainId(stainId)?.name.toLowerCase().includes(lowerQuery));
        };
        return this.data.palettes.filter((p) => p.name.toLowerCase().includes(lowerQuery) ||
            p.description.toLowerCase().includes(lowerQuery) ||
            p.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)) ||
            matchesDye(p));
    }
    // ============================================================================
    // Random Selection
    // ============================================================================
    /**
     * Get a random preset, optionally filtered by category
     * @param category - Optional category to filter by
     * @returns Random preset, or undefined if no presets exist
     *
     * @example
     * ```typescript
     * // Random from all presets
     * const random = presetService.getRandomPreset();
     *
     * // Random job preset
     * const randomJob = presetService.getRandomPreset('jobs');
     * ```
     */
    getRandomPreset(category) {
        const pool = category ? this.getPresetsByCategory(category) : this.data.palettes;
        if (pool.length === 0) {
            return undefined;
        }
        return pool[Math.floor(Math.random() * pool.length)];
    }
    // ============================================================================
    // Dye Resolution (requires DyeService)
    // ============================================================================
    /**
     * Get preset with resolved Dye objects
     * Requires a DyeService instance to resolve dye IDs to full Dye objects
     *
     * @param id - Preset identifier
     * @param dyeService - DyeService instance for resolving dye IDs
     * @returns Resolved preset with full Dye objects, or undefined if preset not found
     *
     * @example
     * ```typescript
     * const dyeService = new DyeService(dyeDatabase);
     * const resolved = presetService.getPresetWithDyes('job-rdm', dyeService);
     *
     * if (resolved) {
     *   resolved.resolvedDyes.forEach(dye => {
     *     if (dye) {
     *       console.log(`${dye.name}: ${dye.hex}`);
     *     }
     *   });
     * }
     * ```
     */
    getPresetWithDyes(id, dyeService) {
        const preset = this.getPreset(id);
        if (!preset) {
            return undefined;
        }
        return {
            ...preset,
            // 5.0: presets.json 2.0.0 stores stainIDs (never derived from ranges)
            resolvedDyes: preset.dyes.map((stainId) => dyeService.getByStainId(stainId)),
        };
    }
}
//# sourceMappingURL=PresetService.js.map